import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import EmailTemplateModal from "../../email/EmailTemplateModal";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetTicketTemplateQuery } from "../../../Services/Messages/Messages";
import axios from "axios";
import "./Messages.scss";

interface IPROPS {
  customerNotes: any;
  fetchingNotes: any;
  showCompose: boolean;
  setShowCompose: (show: boolean) => void;
}

const customModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "700px",
    maxWidth: "90vw",
    height: "600px",
    maxHeight: "90vh",
    borderRadius: "16px",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(4px)",
    zIndex: 1000,
  },
};

const stripHtmlTags = (html: string) => {
  const doc = new window.DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const ExternalMessage: React.FC<IPROPS> = ({
  customerNotes,
  fetchingNotes,
  showCompose,
  setShowCompose,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [storeTemplateData, setStoreTemplateData] = useState<any[]>([]);
  const [selectedTemplateDescription, setSelectedTemplateDescription] =
    useState<any>("");
  const [value, setValue] = useState<string>("");
  const [initials, setInitials] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [files, setFiles] = useState([]);
  const [templateAttachment, setTemplateAttachment] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { data: getTemplate } = useGetTicketTemplateQuery({});
  const { uuid } = useParams();

  const fetchingTenant = window.location.hostname.split(".")[0];
  const apiPort = process.env.REACT_APP_API_PROTOCOL;

  const customerNoteSubmitHandler = async () => {
    if (!value.trim() && !selectedTemplateDescription?.content) {
      toast.error("Please enter a message");
      return;
    }

    setIsLoading(true);

    const customerNoteObj = {
      attachments: files,
      template: selectedTemplateDescription.id || "",
      content: stripHtmlTags(selectedTemplateDescription?.content || value),
      ticket: uuid,
    };

    try {
      const response = await axios.post(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/tickets/customer-note/`,
        customerNoteObj,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
        },
      );

      if (response.data) {
        fetchingNotes();
        setFiles([]);
        setTemplateAttachment([]);
        setSelectedTemplateDescription({});
        setValue("");
        setShowCompose(false);
        toast.success("Note sent successfully");
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const smtpError =
        Array.isArray(detail?.["server-configuration"]) &&
        detail["server-configuration"].includes(
          "smtp server configuration not found for this team",
        );

      const unauthorizedNoteError =
        typeof detail === "string" &&
        detail === "You are not authorized to send customer notes";

      if (smtpError) {
        toast.error("SMTP configuration is missing for this team.");
      } else if (unauthorizedNoteError) {
        toast.error("You are not authorized to send customer notes.");
      } else {
        toast.error("Failed to send external note.");
      }

      console.error("External note save failed:", error.response?.data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const newFiles = Array.from(event.target.files);
    const filePromises = newFiles.map((file: any) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const fileData = {
            file_name: file.name,
            file: e.target.result.split(",")[1],
          };
          resolve(fileData);
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
    });

    try {
      const fileDataArray = await Promise.all(filePromises);
      setFiles((prevFiles) => [...prevFiles, ...fileDataArray]);
    } catch (error) {
      toast.error("An error occurred while uploading files. Please try again.");
    }
  };

  const handleDownloadFile = (fileId) => {
    window.open(`${fileId}`, "_blank");
  };

  const handleDeleteFile = (fileToDelete) => () => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToDelete));
  };

  function formatDate(dateString: string) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    const storedUsername = localStorage.getItem("circleone-username") || "";
    const nameParts = storedUsername.split(" ");
    const generatedInitials = nameParts
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase();

    setInitials(generatedInitials);
    setName(storedUsername);
  }, []);

  useEffect(() => {
    if (getTemplate) {
      setStoreTemplateData(getTemplate);
    }
  }, [getTemplate]);

  useEffect(() => {
    if (selectedTemplateDescription) {
      setValue(stripHtmlTags(selectedTemplateDescription.content));
    }
  }, [selectedTemplateDescription]);

  const getAvatarColor = (index: number) => {
    const colors = ["avatar-blue", "avatar-green", "avatar-purple"];
    return colors[index % colors.length];
  };

  const currentValue = selectedTemplateDescription?.content
    ? stripHtmlTags(selectedTemplateDescription.content)
    : value;


  const getInitials = (userName: any) => {
    return userName.split(" ")
      .map((part: any) => part.charAt(0))
      .join("")
      .toUpperCase();
  }
  const getName = (str: any) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  return (
    <div>
      {showCompose && (
        <div className="compose-note">
          <textarea
            className="compose-textarea"
            value={currentValue}
            onChange={(e) => {
              if (selectedTemplateDescription?.content) {
                setSelectedTemplateDescription({
                  ...selectedTemplateDescription,
                  content: e.target.value,
                });
              } else {
                setValue(e.target.value);
              }
            }}
            rows={3}
            placeholder="Type your message here..."
          />

          {files.length > 0 && (
            <div className="compose-attachments">
              {files.map((file, index) => (
                <div key={index} className="custom-chip">
                  <i className="bi bi-paperclip attachment-icon"></i>
                  <span>{file.file_name}</span>
                  <i
                    className="bi bi-x chip-delete-icon"
                    onClick={handleDeleteFile(file)}
                  ></i>
                </div>
              ))}
            </div>
          )}

          {templateAttachment?.length > 0 && (
            <div className="compose-attachments">
              {templateAttachment?.map((file, index) => (
                <div key={index} className="custom-chip">
                  <i className="bi bi-paperclip attachment-icon"></i>
                  <span>{file.file_name}</span>
                  <i
                    className="bi bi-x chip-delete-icon"
                    onClick={() => {
                      setTemplateAttachment((prev) =>
                        prev.filter((f) => f.id !== file.id),
                      );
                    }}
                  ></i>
                </div>
              ))}
            </div>
          )}

          <div className="compose-footer">
            <div className="compose-actions">
              <label
                htmlFor="attachFileInputExternal"
                className={`action-btn ${!currentValue ? "disabled" : ""}`}
                title="Attach files"
              >
                <i className="bi bi-paperclip"></i>
                <input
                  type="file"
                  id="attachFileInputExternal"
                  multiple
                  onChange={handleFileChange}
                  disabled={!currentValue}
                />
              </label>

              <button
                className="action-btn"
                onClick={openModal}
                title="Use template"
              >
                <i className="fas fa-mail-bulk"></i>
              </button>
            </div>

            <div className="compose-submit">
              <button
                className="btn-send"
                onClick={customerNoteSubmitHandler}
                disabled={isLoading || !currentValue.trim()}
              >
                {isLoading ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReactModal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={customModalStyles}
        contentLabel="ticket templates"
        shouldCloseOnOverlayClick={false}
      >
        <EmailTemplateModal
          closeModal={closeModal}
          setSelectedTemplateDescription={setSelectedTemplateDescription}
          setTemplateAttachment={setTemplateAttachment}
          storeTemplateData={storeTemplateData}
          templateName={"Ticket"}
        />
      </ReactModal>

      <div className="notes-list">
        {customerNotes && customerNotes.length > 0 ? (
          customerNotes.map((el: any, index: number) => (
            <div className="note-item" key={el.id}>
              <div className="note-avatar">
                <div className={`avatar-circle ${getAvatarColor(index)}`}>
                  {getInitials(el?.created_by.username)}
                </div>
              </div>

              <div className="note-content">
                <div className="note-header">
                  <div className="note-author">
                    <span className="author-name">   {getName(el?.created_by.username.split("@")[0])}</span>
                    <span className="note-dot"></span>
                    <span className="note-timestamp">
                      {el?.created ? formatDate(el.created) : "Just now"}
                    </span>
                  </div>
                  <div className="note-actions">
                    <button title="More actions">
                      <i className="bi bi-three-dots"></i>
                    </button>
                  </div>
                </div>

                <p className="note-text">{stripHtmlTags(el?.content)}</p>

                {el.attachments?.length > 0 && (
                  <div className="note-attachments">
                    {el.attachments.map((attachment, idx) => (
                      <div
                        key={attachment.id}
                        className="attachment-chip"
                        onClick={() => handleDownloadFile(attachment.file)}
                      >
                        <i className="bi bi-paperclip attachment-icon"></i>
                        <span>
                          {attachment.file_name || `Attachment ${idx + 1}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>No external notes yet. Click "Add Note" to create one.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExternalMessage;